<template>
  <v-dialog
      v-model="showEditor"
      persistent
      max-width="600px"
      @input="openEditorHandler"
  >
    <v-card class="accent">
      <v-card-title
          style="
              background-color: #47bd96;
              color: white;
              letter-spacing: 0.03rem;
              height: 40px;
            "
          :style="
              $vuetify.breakpoint.xsOnly ? 'text-align: center !important;' : ''
            "
      >
        {{ editMode ? `Edit ${tenantData.name}` : 'Add Tenant' }}
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row
              justify="center"
          >
            <v-col
                cols="12"
                sm="6"
                md="12"
            >
              <v-text-field
                  ref="tenantName"
                  label="Name"
                  v-model="localTenantData.name"
                  filled
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
              />
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col
                cols="12"
                sm="6"
                md="12"
            >
              <input-wrapper-2
                  label="Parent"
                  info="Select a parent Tenant for which you want to link the tenant to."
                  :label-background-light="'#D2E2DE'"
                  :label-background-dark="'#162627'"
                  dense
                  full-width
              >
                <v-select
                    ref="selectParentTenantInput"
                    v-model="localTenantData.parent"
                    :items="parentSelectOptions"
                    item-text="name"
                    item-value="id"
                    placeholder="Select Tenant"
                    dense
                    filled
                    solo
                    hide-details
                    style="width: 100%; margin: 4px 0;"
                ></v-select>
                <v-btn icon small @click="localTenantData.parent=null">
                  <v-icon small color="#d50101">
                    fas fa-times
                  </v-icon>
                </v-btn>
              </input-wrapper-2>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col
                cols="12"
                sm="6"
                md="12"
            >
              <input-wrapper2
                  label="Properties"
                  container-class="properties-cont"
                  full-width
                  flex
              >
                <div class="properties-list">
                  <template v-if="!!localTenantData.properties">
                    <div
                        v-for="(value, key) in localTenantData.properties"
                        :key="`property-key-${key}`"
                        class="property-item"
                    >
                      <div class="ml-6">{{ key }}</div>
                      <div> :</div>
                      <div>{{ value }}</div>
                      <v-spacer/>
                    </div>
                  </template>
                  <template v-else>
                    <div class="no-results">No Properties found!</div>
                  </template>
                  <div class="new-property">
                    <div class="new-property__key">
                      <v-text-field v-model="propertyEntry.key" filled placeholder="key" dense hide-details
                                    width="24px"/>
                    </div>
                    :
                    <div class="new-property__value">
                      <v-text-field v-model="propertyEntry.value" filled placeholder="value" dense hide-details/>
                    </div>
                    <v-btn x-small text dense @click="addPropertyHandler">
                      + Add
                    </v-btn>
                  </div>
                </div>
                <div class="property-error">{{ propertyError }}</div>
              </input-wrapper2>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn plain @click="closeEditorHandler">Cancel</v-btn>
        <span @mouseenter="touchError">
          <v-btn
              color="primary"
              :disabled="!canSave"
              :loading="loading"
              @click="saveTenantHandler"
          >
            {{ !!editMode ? 'Save' : 'Add' }}
          </v-btn>
          </span>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InputWrapper2 from "@/components/UI/InputWrapper2.vue";
import {cloneDeep} from "lodash";
import {mapGetters} from "vuex";

const defaultTenantData = {
  id: null,
  name: '',
  parent: '',
  properties: {},
};

export default {
  name: 'TenantEditor',
  components: {InputWrapper2},
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    tenantData: {
      type: Object,
      default: () => defaultTenantData
    },
    tenantsList: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    localTenantData: {
      name: '',
      parent: '',
      properties: {},
    },
    propertyEntry: {
      key: '',
      value: '',
    },
    propertyError: '',
    rules: {
      required: (v) => !!v || "Required",
    },
    selectedParent: null,
  }),
  computed: {
    ...mapGetters("notifications", ["getTenants"]),
    showEditor: {
      get() {
        this.openEditorHandler();
        return this.showDialog;
      },
      set() {
        this.$emit('toggle');
      }
    },
    editMode() {
      return !!this.tenantData && !!this.tenantData.id && (this.tenantData.id.length > 0);
    },
    parentSelectOptions() {
      if (this.getTenants && this.getTenants.length > 0) {
        return this.getTenants.map(tenant => {
          return {name: tenant.name, id: tenant.id};
        });
      }
      return [];
    },
    canSave() {
      return this.localTenantData.name.length > 0;
    }
  },
  methods: {
    /**
     * Check if the user has entered a name for the Tenant
     * and display the corresponding error message accordingly
     */
    touchError() {
      this.$refs.tenantName.validate(true);
    },
    /**
     * Handle the open event of the Tenant Editor and check if any tenantData prop is present
     * then copy it to the local tenant data for editing
     */
    openEditorHandler() {
      if (
          (this.editMode === true) &&
          (this.showDialog === true) &&
          (!!this.tenantData) &&
          (!!this.tenantData.id) &&
          (this.tenantData.id.length > 0)
      ) {
        this.localTenantData = cloneDeep(this.tenantData);
      }
    },
    /**
     * Handle the Tenant Editor close trigger
     */
    closeEditorHandler() {
      this.$refs.tenantName.reset();
      this.$emit('close');
      this.localTenantData = {
        name: '',
        parent: '',
        channels: [],
        properties: {},
      };
      this.propertyEntry = {
        key: '',
        value: '',
      };
      this.propertyError = '';
    },
    /**
     * Emit a save event to the parent with the Tenant data to save
     */
    saveTenantHandler() {
      const newTenantData = cloneDeep(this.localTenantData);
      this.$emit('save', newTenantData);
      this.closeEditorHandler();
    },
    /**
     * Handle the addition of a new property in the tenant's data
     * - in the event of a duplicate property addition attempt, do not proceed
     * to add the new property and display the corresponding error message
     */
    addPropertyHandler() {
      if (!this.localTenantData.properties[this.propertyEntry.key]) {
        this.localTenantData.properties = {
          ...this.localTenantData.properties,
          [this.propertyEntry.key]: this.propertyEntry.value
        };
        this.propertyError = '';
      } else {
        this.propertyError = 'Property already exists!';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.properties-cont {
  display: flex !important;
  width: 100%;

  .properties-list {
    padding: 8px;

    .property-item {
      display: flex;
      flex-flow: row;
      padding: 4px;
      gap: 8px;
      justify-content: space-around;
      cursor: default;
    }
  }
}

.new-property {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-top: 8px;
}

.property-error {
  color: red;
  font-style: italic;
  padding-left: 8px;
  min-height: 24px;
}

.main-dark {
  .property-item:hover {
    background-color: rgba(250, 250, 250, 0.05);
  }
}

.main-light {
  .property-item:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}
</style>